import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, VisitBooking, VisitSlotSummary } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const CUSTODIAL_ROLES = ['CUSTODIAL_ADMIN'];
const FACILITIES = [
  { code: 'MP-MAIN', name: 'Montagne Posée Prison' },
  { code: 'MP-REMAND', name: 'Montagne Posée Remand Wing' },
  { code: 'COETIVY', name: 'Coëtivy Island Work Camp' },
];

function isoDatePlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Prisons — visitor bookings (M07): async CRO vetting, manual-review decision, and counter check-in/no-show. */
export function Visits() {
  const { persona } = useAuth();
  const isCustodialAdmin = persona ? persona.roles.some((r) => CUSTODIAL_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showBook, setShowBook] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m07/visits?${params.toString()}`;
  }, [status]);

  const listQ = useApiQuery<Page<VisitBooking>>(path);

  return (
    <div>
      <PageHeader
        title="Visit Bookings"
        subtitle="Visitor bookings by the inmate's register number — vetting runs inline against M05 (M07)."
        actions={
          <div className="flex items-center gap-2">
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="PENDING_VETTING">Pending vetting</option>
              <option value="MANUAL_REVIEW">Manual review</option>
              <option value="APPROVED">Approved</option>
              <option value="DENIED">Denied</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="COMPLETED">Completed</option>
              <option value="NO_SHOW">No-show</option>
            </select>
            <button className="btn-primary" onClick={() => setShowBook(true)}>
              + Book a visit
            </button>
          </div>
        }
      />

      {!isCustodialAdmin ? <RoleNotice roles={CUSTODIAL_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No visit bookings match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Visitor</th>
                <th className="table-head">Relationship</th>
                <th className="table-head">Vetting hits</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((v) => (
                <tr key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(v.id)}>
                  <td className="table-cell font-medium">
                    {v.visitorFullName}
                    <div className="font-mono text-[11px] text-slate-400">{v.visitorNin}</div>
                  </td>
                  <td className="table-cell text-xs text-slate-500">{v.relationship ?? '—'}</td>
                  <td className="table-cell text-xs text-slate-500">{v.vettingHits ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={v.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <VisitDetailModal
          id={selectedId}
          canAct={isCustodialAdmin}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}

      {showBook ? (
        <BookVisitModal
          onClose={() => setShowBook(false)}
          onDone={() => {
            setShowBook(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function VisitDetailModal({
  id,
  canAct,
  onClose,
  onChanged,
}: {
  id: string;
  canAct: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const q = useApiQuery<VisitBooking>(`/api/v1/m07/visits/${id}`);
  const v = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [decision, setDecision] = useState<'APPROVE' | 'DENY' | null>(null);

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={v ? `Visit — ${v.visitorFullName}` : 'Visit booking'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {v ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={v.status} />} />
            <Detail label="Visitor NIN" value={v.visitorNin} mono />
            <Detail label="Relationship" value={v.relationship ?? '—'} />
            <Detail label="Vetting hits" value={v.vettingHits ?? '—'} />
          </div>
          {v.vettingNotes ? <Detail label="Vetting notes" value={v.vettingNotes} /> : null}

          {canAct && (v.status === 'PENDING_VETTING' || v.status === 'MANUAL_REVIEW') ? (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m07/visits/${id}/re-vet`))}>
              Re-run CRO vetting check
            </button>
          ) : null}

          {v.status === 'MANUAL_REVIEW' ? (
            canAct ? (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-semibold text-slate-700">Manual review decision</div>
                <div className="flex gap-2">
                  <button className={`btn-success ${decision === 'APPROVE' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('APPROVE')}>
                    Approve
                  </button>
                  <button className={`btn-danger ${decision === 'DENY' ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setDecision('DENY')}>
                    Deny
                  </button>
                </div>
                {decision ? (
                  <>
                    <Field label="Notes (optional)">
                      <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </Field>
                    <button
                      className={decision === 'APPROVE' ? 'btn-success' : 'btn-danger'}
                      disabled={busy}
                      onClick={() =>
                        call(() => api.post(`/api/v1/m07/visits/${id}/decision`, { decision, ...(notes.trim() ? { notes: notes.trim() } : {}) }))
                      }
                    >
                      {busy ? 'Submitting…' : `Confirm ${decision.toLowerCase()}`}
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <RoleNotice roles={CUSTODIAL_ROLES} />
            )
          ) : null}

          {canAct && v.status === 'APPROVED' ? (
            <div className="flex gap-2">
              <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m07/visits/${id}/complete`))}>
                Mark completed
              </button>
              <button className="btn-danger" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m07/visits/${id}/no-show`))}>
                Mark no-show
              </button>
            </div>
          ) : null}

          {(v.status === 'PENDING_VETTING' || v.status === 'MANUAL_REVIEW' || v.status === 'APPROVED') && canAct ? (
            <button className="btn-secondary" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m07/visits/${id}/cancel`))}>
              Cancel booking
            </button>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Booked {formatDateTime(v.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function BookVisitModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [facilityCode, setFacilityCode] = useState(FACILITIES[0]!.code);
  const [from] = useState(isoDatePlusDays(0));
  const [to] = useState(isoDatePlusDays(14));
  const [slots, setSlots] = useState<VisitSlotSummary[] | null>(null);
  const [slotId, setSlotId] = useState('');
  const [regNo, setRegNo] = useState('');
  const [visitorFullName, setVisitorFullName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const findSlots = async () => {
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams({ facilityCode, from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z`, limit: '20' });
      const page = await api.get<Page<VisitSlotSummary>>(`/api/v1/m07/visit-slots?${params.toString()}`);
      setSlots(page.items);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to load slots');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m07/visits/book', {
        regNo: regNo.trim(),
        slotId,
        visitorFullName: visitorFullName.trim(),
        ...(relationship.trim() ? { relationship: relationship.trim() } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to book');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Book a visit" onClose={onClose} wide>
      <div className="space-y-4">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-400">
          The API books using the signed-in identity's own NIN as the visitor of record — there is no staff on-behalf-of-another-citizen
          parameter on this endpoint, so this books the signed-in officer as the visitor.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Facility">
            <select className="field-input" value={facilityCode} onChange={(e) => setFacilityCode(e.target.value)}>
              {FACILITIES.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="flex items-end">
            <button className="btn-secondary" disabled={busy} onClick={findSlots}>
              Find open slots (next 14 days)
            </button>
          </div>
        </div>

        {slots ? (
          slots.length === 0 ? (
            <p className="text-sm text-slate-400">No open slots at this facility in the next 14 days.</p>
          ) : (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {slots.map((s) => (
                <label key={s.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input type="radio" name="slot" checked={slotId === s.id} onChange={() => setSlotId(s.id)} />
                  {formatDateTime(s.startsAt)} — {s.remainingCapacity} of {s.capacity} spaces free
                </label>
              ))}
            </div>
          )
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Inmate register number">
            <input className="field-input font-mono" value={regNo} onChange={(e) => setRegNo(e.target.value)} placeholder="e.g. SPS-2026-000123" />
          </Field>
          <Field label="Visitor full name">
            <input className="field-input" value={visitorFullName} onChange={(e) => setVisitorFullName(e.target.value)} />
          </Field>
        </div>
        <Field label="Relationship (optional)">
          <input className="field-input" value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="e.g. Spouse" />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !slotId || !regNo.trim() || !visitorFullName.trim()} onClick={submit}>
            {busy ? 'Booking…' : 'Book visit'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
